import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Icons } from '../../Icon.jsx';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import Input from 'aldoo-ra/Input';
import { TailwindParser } from '../../../utils/TailwindParser.js';
import { stylePlugins, getPluginForProperty, getPropertyDefinition } from '../../style-plugins/index.js';
import { ALL_STATES, getStatePrefix } from '../../pseudo-classes.js';
import { resolveColor } from '../utils.js';
import PropertyRow from './PropertyRow.jsx';
import StylePluginItem from './StylePluginItem.jsx';
import { BREAKPOINTS } from '../../../constants.js';

const VariantPropertiesPanel = ({ activeKey, classString, onChange, onToggle, currentState, onStateChange, activeBreakpoint = 'default', onOverridesUpdate, breakpointCopyRequest, onBreakpointCopyComplete }) => {
    // currentState managed by parent (pseudo-class state: normal, hover, etc.)
    // activeBreakpoint managed by parent

    const [stateArgs, setStateArgs] = useState('');
    
    // stateData structure: { [breakpoint]: { [state]: { activeProps, disabledProps, manualProps, manualPlugins } } }
    const [stateData, setStateData] = useState({}); 
    const stateDataRef = useRef(stateData);
    useEffect(() => { 
        stateDataRef.current = stateData; 
        
        // Notify parent about overrides for header UI
        if (onOverridesUpdate) {
            const map = {};
            Object.keys(stateData).forEach(bp => {
                const hasOverrides = stateData[bp] && Object.keys(stateData[bp]).some(st => 
                    stateData[bp][st].activeProps && Object.keys(stateData[bp][st].activeProps).length > 0
                );
                map[bp] = hasOverrides;
            });
            onOverridesUpdate(map);
        }
    }, [stateData, onOverridesUpdate]);

    const [activeProps, setActiveProps] = useState({});
    const [disabledProps, setDisabledProps] = useState({});
    const [manualProps, setManualProps] = useState([]);
    const [manualPlugins, setManualPlugins] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddPlugin, setShowAddPlugin] = useState(false);
    const isLocalUpdate = useRef(false);
    const [openPlugins, setOpenPlugins] = useState([]);
    const dragRef = useRef({ isDragging: false });

    // Calculate effective props (Inheritance Waterfall)
    const effectiveProps = useMemo(() => {
        const merged = {};
        // Use activeProps from stateData directly
        
        const sortedBps = Object.keys(BREAKPOINTS).sort((a, b) => BREAKPOINTS[a] - BREAKPOINTS[b]);
        const activeWidth = BREAKPOINTS[activeBreakpoint] || 0;

        // Start with default
        const bpsToMerge = ['default', ...sortedBps.filter(bp => bp !== 'default' && BREAKPOINTS[bp] <= activeWidth)];
        
        bpsToMerge.forEach(bp => {
            const bpData = stateData[bp];
            if (bpData && bpData[currentState]) {
                const props = bpData[currentState].activeProps;
                if (props) Object.assign(merged, props);
            }
        });
        
        return merged;
    }, [stateData, activeBreakpoint, currentState]);

    const handlePluginDragStart = (e, startIndex) => {
        if (e.target.closest('button') || e.target.closest('.relative')) return;
        const startY = e.clientY;
        const initialOpen = new Set(openPlugins);
        dragRef.current = { isDragging: false, startY };
        const handleMouseMove = (mv) => {
            const deltaY = mv.clientY - startY;
            if (Math.abs(deltaY) > 5) {
                dragRef.current.isDragging = true;
                document.body.style.cursor = 'ns-resize';
                document.body.style.userSelect = 'none';
            }
            if (dragRef.current.isDragging) {
                const step = 20; const count = Math.floor(Math.abs(deltaY) / step);
                const isExpandAction = deltaY > 0;
                const next = new Set(initialOpen);
                for (let i = startIndex; i < activePlugins.length; i++) {
                    const id = activePlugins[i].id; const offset = i - startIndex;
                    if (offset <= count) {
                        if (isExpandAction) next.add(id); else next.delete(id);
                    } else {
                        if (initialOpen.has(id)) next.add(id); else next.delete(id);
                    }
                }
                setOpenPlugins(Array.from(next));
            }
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setTimeout(() => { dragRef.current.isDragging = false; }, 0); 
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const saveCurrentStateData = useCallback(() => {
        setStateData(prev => ({
            ...prev,
            [activeBreakpoint]: {
                ...(prev[activeBreakpoint] || {}),
                [currentState]: {
                    activeProps,
                    disabledProps,
                    manualProps,
                    manualPlugins
                }
            }
        }));
    }, [activeBreakpoint, currentState, activeProps, disabledProps, manualProps, manualPlugins]);

    useEffect(() => {
        const bpData = stateData[activeBreakpoint] || {};
        const stData = bpData[currentState] || {};

        setActiveProps(stData.activeProps || {});
        setDisabledProps(stData.disabledProps || {});
        setManualProps(stData.manualProps || []);
        setManualPlugins(stData.manualPlugins || []);
    }, [activeBreakpoint, currentState, stateData]);

    // Handle breakpoint copy request
    useEffect(() => {
        if (!breakpointCopyRequest) return;

        const { from, to } = breakpointCopyRequest;
        const sourceData = stateData[from]?.[currentState];

        if (sourceData) {
            const updatedStateData = {
                ...stateData,
                [to]: {
                    ...(stateData[to] || {}),
                    [currentState]: {
                        activeProps: { ...sourceData.activeProps },
                        disabledProps: { ...sourceData.disabledProps },
                        manualProps: [...sourceData.manualProps],
                        manualPlugins: [...sourceData.manualPlugins]
                    }
                }
            };

            setStateData(updatedStateData);

            // Regenerate class string with new stateData
            const allClasses = [];
            Object.keys(updatedStateData).forEach(bp => {
                const bpData = updatedStateData[bp];
                if (!bpData) return;

                Object.keys(bpData).forEach(state => {
                    const data = bpData[state];
                    if (!data || !data.activeProps || Object.keys(data.activeProps).length === 0) return;

                    const stateClasses = TailwindParser.generate(data.activeProps);
                    if (!stateClasses) return;

                    const statePrefix = getStatePrefix(state);
                    const bpPrefix = bp === 'default' ? '' : `${bp}:`;
                    const fullPrefix = `${bpPrefix}${statePrefix}`;

                    const prefixedClasses = stateClasses.split(' ')
                        .filter(c => c)
                        .map(c => fullPrefix + c)
                        .join(' ');

                    allClasses.push(prefixedClasses);
                });
            });

            onChange(allClasses.join(' '));
        }

        onBreakpointCopyComplete?.();
    }, [breakpointCopyRequest, currentState, stateData, onChange, onBreakpointCopyComplete]);

    useEffect(() => {
        if (isLocalUpdate.current) {
            isLocalUpdate.current = false;
            return;
        }

        const classes = (classString || '').split(' ').filter(c => c);
        const newStateData = {};

        classes.forEach(cls => {
            let bp = 'default';
            let processedClass = cls;
            
            const parts = processedClass.split(':');
            const possibleBP = parts[0];
            if (Object.keys(BREAKPOINTS).includes(possibleBP) && possibleBP !== 'default') {
                 bp = possibleBP;
                 processedClass = parts.slice(1).join(':');
            }

            let state = 'normal';
            const matchedState = ALL_STATES.find(s => s.prefix && processedClass.startsWith(s.prefix));
            if (matchedState) {
                state = matchedState.value;
                processedClass = processedClass.substring(matchedState.prefix.length);
            }

            const props = TailwindParser.parse(processedClass);
            
            if (!newStateData[bp]) newStateData[bp] = {};
            if (!newStateData[bp][state]) newStateData[bp][state] = { activeProps: {}, disabledProps: {}, manualProps: [], manualPlugins: [] };
            
            Object.assign(newStateData[bp][state].activeProps, props);
        });

        const mergedStateData = { ...stateDataRef.current };
        
        Object.keys(mergedStateData).forEach(bp => {
            Object.keys(mergedStateData[bp]).forEach(st => {
                mergedStateData[bp][st].activeProps = {};
            });
        });
        
        Object.keys(newStateData).forEach(bp => {
            if (!mergedStateData[bp]) mergedStateData[bp] = {};
            Object.keys(newStateData[bp]).forEach(st => {
                if (!mergedStateData[bp][st]) mergedStateData[bp][st] = { activeProps: {}, disabledProps: {}, manualProps: [], manualPlugins: [] };
                Object.assign(mergedStateData[bp][st].activeProps, newStateData[bp][st].activeProps);
            });
        });
        
        setStateData(mergedStateData);
    }, [classString]);

    const updateClassString = (newActive, newDisabled, newManual, newPlugins) => {
        const nextActive = newActive !== undefined ? newActive : activeProps;
        const nextDisabled = newDisabled !== undefined ? newDisabled : disabledProps;
        const nextManual = newManual !== undefined ? newManual : manualProps;
        const nextPlugins = newPlugins !== undefined ? newPlugins : manualPlugins;

        const updatedStateData = {
            ...stateData,
            [activeBreakpoint]: {
                ...(stateData[activeBreakpoint] || {}),
                [currentState]: {
                    activeProps: nextActive,
                    disabledProps: nextDisabled,
                    manualProps: nextManual,
                    manualPlugins: nextPlugins
                }
            }
        };
        setStateData(updatedStateData);

        const allClasses = [];
        
        Object.keys(updatedStateData).forEach(bp => {
            const bpData = updatedStateData[bp];
            if (!bpData) return;
            
            Object.keys(bpData).forEach(state => {
                const data = bpData[state];
                if (!data || !data.activeProps || Object.keys(data.activeProps).length === 0) return;

                const stateClasses = TailwindParser.generate(data.activeProps);
                if (!stateClasses) return;

                const statePrefix = getStatePrefix(state);
                const bpPrefix = bp === 'default' ? '' : `${bp}:`;
                const fullPrefix = `${bpPrefix}${statePrefix}`;
                
                const prefixedClasses = stateClasses.split(' ')
                    .filter(c => c)
                    .map(c => fullPrefix + c)
                    .join(' ');

                allClasses.push(prefixedClasses);
            });
        });

        onChange(allClasses.join(' '));
    };

    const handlePropChange = (key, val) => {
        isLocalUpdate.current = true;
        let newManual = manualProps;
        if (!manualProps.includes(key)) { newManual = [...manualProps, key]; setManualProps(newManual); }
        const newActive = { ...activeProps };
        if (!val) delete newActive[key]; else newActive[key] = val;
        setActiveProps(newActive);
        let newDisabled = disabledProps;
        if (disabledProps[key]) { newDisabled = { ...disabledProps }; delete newDisabled[key]; setDisabledProps(newDisabled); }
        updateClassString(newActive, newDisabled, newManual);
    };

    const handleToggleProperty = (key) => {
        isLocalUpdate.current = true;
        let newManual = manualProps;
        if (!manualProps.includes(key)) { newManual = [...manualProps, key]; setManualProps(newManual); }
        const newActive = { ...activeProps }; const newDisabled = { ...disabledProps };
        if (activeProps[key]) { newDisabled[key] = activeProps[key]; delete newActive[key]; } 
        else if (disabledProps[key]) { newActive[key] = disabledProps[key]; delete newDisabled[key]; }
        setActiveProps(newActive); setDisabledProps(newDisabled);
        updateClassString(newActive, newDisabled, newManual);
    };

    const handleRemoveProperty = (key) => {
        isLocalUpdate.current = true;
        const newManual = manualProps.filter(k => k !== key); setManualProps(newManual);
        const newDisabled = { ...disabledProps }; delete newDisabled[key]; setDisabledProps(newDisabled);
        const newActive = { ...activeProps }; delete newActive[key]; setActiveProps(newActive);
        updateClassString(newActive, newDisabled, newManual);
    };

    const handleAddProperty = (key) => {
        const def = getPropertyDefinition(key);
        let val = def?.default;
        if (val === undefined || val === null) {
            if (def.options && def.options.length > 0) {
                const firstOption = def.options[0];
                val = (typeof firstOption === 'object' && firstOption !== null && 'value' in firstOption) ? firstOption.value : firstOption;
            } else { val = 'black'; }
        }
        handlePropChange(key, val);
    };

    const activePlugins = useMemo(() => {
        const pluginIds = new Set(manualPlugins);
        [...Object.keys(effectiveProps), ...Object.keys(disabledProps), ...manualProps].forEach(key => {
            const plugin = getPluginForProperty(key);
            if (plugin) pluginIds.add(plugin.id);
        });
        return stylePlugins.filter(p => pluginIds.has(p.id));
    }, [effectiveProps, disabledProps, manualProps, manualPlugins]);

    useEffect(() => {
        setOpenPlugins(prev => {
            const currentIds = new Set(prev); let changed = false;
            activePlugins.forEach(p => { if (!currentIds.has(p.id)) { currentIds.add(p.id); changed = true; } });
            return changed ? Array.from(currentIds) : prev;
        });
    }, [activePlugins]);

    const availablePlugins = useMemo(() => {
        const activeIds = activePlugins.map(p => p.id);
        return stylePlugins.filter(p => !activeIds.includes(p.id));
    }, [activePlugins]);

    const handleAddPlugin = (pluginId) => {
        const newManualPlugins = [...manualPlugins, pluginId];
        setManualPlugins(newManualPlugins); setShowAddPlugin(false);
        updateClassString(undefined, undefined, undefined, newManualPlugins);
    };

    const handleRemovePlugin = (pluginId) => {
        const newManualPlugins = manualPlugins.filter(id => id !== pluginId); setManualPlugins(newManualPlugins);
        const plugin = stylePlugins.find(p => p.id === pluginId); if (!plugin) return;
        const newActive = { ...activeProps }; const newDisabled = { ...disabledProps };
        const propertyKeys = Array.isArray(plugin.properties) ? plugin.properties : Object.keys(plugin.properties);
        propertyKeys.forEach(key => { if (newActive[key]) delete newActive[key]; if (newDisabled[key]) delete newDisabled[key]; });
        const newManual = manualProps.filter(key => !propertyKeys.includes(key));
        setActiveProps(newActive); setDisabledProps(newDisabled); setManualProps(newManual);
        updateClassString(newActive, newDisabled, newManual, newManualPlugins);
    };

    const searchResults = useMemo(() => {
        if (!searchQuery) return [];
        const results = []; const lowerQuery = searchQuery.toLowerCase();
        stylePlugins.forEach(plugin => {
            const propertyKeys = typeof plugin.properties === 'object' && !Array.isArray(plugin.properties) ? Object.keys(plugin.properties) : plugin.properties || [];
            propertyKeys.forEach(key => {
                if (activeProps[key] || disabledProps[key]) {
                    const def = getPropertyDefinition(key); const label = def?.label || key;
                    if (label.toLowerCase().includes(lowerQuery) || key.toLowerCase().includes(lowerQuery)) {
                        results.push({ key, pluginName: plugin.label });
                    }
                }
            });
        });
        return results;
    }, [searchQuery, activeProps, disabledProps]);

    const handleCopyPluginState = (pluginId, targetState) => {
        if (targetState === currentState) return;
        const plugin = stylePlugins.find(p => p.id === pluginId); if (!plugin) return;
        const propertyKeys = Array.isArray(plugin.properties) ? plugin.properties : Object.keys(plugin.properties);
        const propsToCopy = {}; const disabledToCopy = {}; const manualToCopy = [];
        propertyKeys.forEach(key => {
            if (activeProps[key] !== undefined) propsToCopy[key] = activeProps[key];
            if (disabledProps[key] !== undefined) disabledToCopy[key] = disabledProps[key];
            if (manualProps.includes(key)) manualToCopy.push(key);
        });

        const targetData = (stateData[activeBreakpoint] || {})[targetState] || { activeProps: {}, disabledProps: {}, manualProps: [], manualPlugins: [] };

        const newTargetActive = { ...targetData.activeProps, ...propsToCopy };
        const newTargetDisabled = { ...targetData.disabledProps, ...disabledToCopy };
        const newTargetManual = [...new Set([...targetData.manualProps, ...manualToCopy])];
        const newTargetPlugins = [...new Set([...targetData.manualPlugins, pluginId])];

        const updatedStateData = {
            ...stateData,
            [activeBreakpoint]: {
                ...(stateData[activeBreakpoint] || {}),
                [currentState]: { activeProps, disabledProps, manualProps, manualPlugins },
                [targetState]: { activeProps: newTargetActive, disabledProps: newTargetDisabled, manualProps: newTargetManual, manualPlugins: newTargetPlugins }
            }
        };

        setStateData(updatedStateData);
        onStateChange(targetState);

        const allClasses = [];
        Object.keys(updatedStateData).forEach(bp => {
            const bpData = updatedStateData[bp];
            Object.keys(bpData).forEach(state => {
                 const data = bpData[state];
                 if (!data || !data.activeProps || Object.keys(data.activeProps).length === 0) return;
                 const stateClasses = TailwindParser.generate(data.activeProps);
                 if (!stateClasses) return;
                 const prefix = getStatePrefix(state);
                 const bpPrefix = bp === 'default' ? '' : `${bp}:`;
                 const fullPrefix = `${bpPrefix}${prefix}`;
                 allClasses.push(stateClasses.split(' ').map(c => fullPrefix + c).join(' '));
            });
        });
        onChange(allClasses.join(' '));
    };

    const selectedState = ALL_STATES.find(s => s.value === currentState);

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-white">
            <div className="p-3 border-b border-slate-200 bg-white flex flex-col gap-2 shrink-0">
                
                <div className="flex justify-between items-center gap-2 h-8">
                {showSearch ? (
                    <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded px-2 py-1 animate-in fade-in slide-in-from-right-2 duration-200">
                         <Icons.Search className="w-3.5 h-3.5 text-gray-500"/>
                         <input
                            autoFocus
                            className="flex-1 bg-transparent border-none outline-none text-xs text-slate-700 placeholder:text-slate-400"
                            placeholder="Search properties..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                         />
                         <button
                            onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                            className="p-0.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                         >
                            <Icons.Remove className="w-3.5 h-3.5"/>
                         </button>
                    </div>
                ) : (
                    <>
                        <h3 className="font-bold text-gray-700 truncate pr-2 select-none flex-1">
                            {activeKey || <span className="text-gray-400 font-normal italic">No key selected</span>}
                        </h3>
                        <div className="flex items-center gap-1">
                            {activeKey && (
                                <>
                                    <div className="flex items-center gap-1 mr-2 border-r border-slate-200 pr-2 h-6">
                                        <DropdownSelect
                                            options={ALL_STATES.map(s => ({ label: s.label, value: s.value, name: s.label, description: s.description }))}
                                            onChange={(val) => {
                                                const selection = Array.isArray(val) ? val[0] : val;
                                                if (selection) onStateChange(selection.value);
                                            }}
                                            searchEnabled={true}
                                            showCheckbox={false}
                                            variant="admin"
                                            className="!p-0 !m-0"
                                            dropdownWidth="12rem"
                                            customTrigger={
                                                <div className="flex items-center justify-center gap-1.5 px-2 py-0.5 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-bold text-slate-600 hover:text-blue-600 transition-colors border border-transparent hover:border-blue-200 group min-w-[4.5rem]">
                                                    <span>{selectedState?.label || 'Normal'}</span>
                                                    {selectedState?.hasArgs && stateArgs && <span className="text-blue-500 font-mono">:{stateArgs}</span>}
                                                    <Icons.ChevronDown className="w-2.5 h-2.5 opacity-50 group-hover:opacity-100" />
                                                </div>
                                            }
                                            onItemRender={(opt) => (
                                                <div className="flex flex-col py-0.5">
                                                    <span className="font-bold text-xs text-slate-700">{opt.label}</span>
                                                    {opt.description && <span className="text-[10px] text-gray-400">{opt.description}</span>}
                                                </div>
                                            )}
                                        />
                                        {selectedState?.hasArgs && (
                                            <Input
                                                variant="admin"
                                                value={stateArgs}
                                                onChange={(e) => setStateArgs(e.target.value)}
                                                placeholder="Args"
                                                className="!py-0 !px-1 text-[10px] h-5 w-12 border-slate-200 focus:border-blue-500 rounded"
                                            />
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setShowSearch(true)}
                                        className="p-1.5 rounded hover:bg-slate-100 text-gray-400 hover:text-blue-500 transition-colors"
                                        title="Search Properties"
                                    >
                                        <Icons.Search className="w-4 h-4"/>
                                    </button>
                                    <div className="w-auto relative">
                                        <DropdownSelect
                                            options={availablePlugins.map(p => ({ label: p.label, value: p.id, icon: p.icon }))}
                                            onChange={(val) => {
                                                const selection = Array.isArray(val) ? val[0] : val;
                                                if (selection) handleAddPlugin(selection.value);
                                            }}
                                            searchEnabled={true}
                                            showCheckbox={false}
                                            variant="admin"
                                            className="!p-0 !m-0"
                                            dropdownWidth="16rem"
                                            customTrigger={
                                                <div
                                                    className="p-1.5 rounded hover:bg-slate-100 text-gray-400 hover:text-blue-500 transition-colors flex items-center justify-center"
                                                    title="Add Style..."
                                                >
                                                    <Icons.Plus className="w-4 h-4"/>
                                                </div>
                                            }
                                            onItemRender={(opt) => (
                                                <div className="flex items-center gap-2">
                                                    {opt.icon && Icons[opt.icon] && React.createElement(Icons[opt.icon], {className:"w-3 h-3 text-blue-500"})}
                                                    <span>{opt.label}</span>
                                                </div>
                                                                                                                                    )}
                                                                                                                                />
                                                                                                                            </div>
                                                                                                                            <button 
                                                                                                                                onClick={() => onToggle(activeKey)}                                        className={`p-1.5 rounded hover:bg-slate-100 ${classString ? 'text-blue-500' : 'text-gray-400'}`}
                                        title={classString ? "Disable Variant (Clear Styles)" : "Variant Empty"}
                                    >
                                        <Icons.Eye className="w-4 h-4"/>
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
                </div>
            </div>

            <div className="flex-1 overflow-y-scroll scrollbar-thin scrollbar-thumb-slate-300">
                {!activeKey ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 text-xs italic bg-slate-50 h-full">
                        Select a key from the list to edit its properties.
                    </div>
                ) : (
                    <>
                        {searchQuery && searchResults.length > 0 ? (
                            <div className="bg-white">
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Results for "{searchQuery}"
                                </div>
                                <div className="p-2 space-y-1">
                                    {searchResults.map(({ key, pluginName }) => (
                                        <PropertyRow
                                            key={key}
                                            propertyKey={key}
                                            pluginName={pluginName}
                                            activeProps={activeProps}
                                            disabledProps={disabledProps}
                                            manualProps={manualProps}
                                            onChange={handlePropChange}
                                            onToggleProperty={handleToggleProperty}
                                            onRemoveProperty={handleRemoveProperty}
                                            activeBreakpoint={activeBreakpoint}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {activePlugins.length === 0 && !searchQuery && (
                                    <div className="p-4 text-center text-xs text-gray-400 italic">
                                        No styles added. Click the (+) button above to add a plugin.
                                    </div>
                                )}
                                                                {activePlugins.map((plugin, index) => (
                                                                    <StylePluginItem 
                                                                        key={plugin.id}
                                                                        plugin={plugin}
                                                                        isOpen={openPlugins.includes(plugin.id)}
                                                                        onToggle={() => {
                                                                            if (!dragRef.current.isDragging) {
                                                                                setOpenPlugins(prev => prev.includes(plugin.id) ? prev.filter(id => id !== plugin.id) : [...prev, plugin.id]);
                                                                            }
                                                                        }}
                                                                        onDragStart={(e) => handlePluginDragStart(e, index)}
                                                                        activeProps={effectiveProps}
                                                                        ownProps={activeProps}
                                                                        disabledProps={disabledProps}
                                                                        manualProps={manualProps}
                                                                        onChange={handlePropChange}
                                                                        onRemove={() => handleRemovePlugin(plugin.id)}
                                                                        onToggleProperty={handleToggleProperty}
                                                                        onRemoveProperty={handleRemoveProperty}
                                                                        onAddProperty={handleAddProperty}
                                                                        onCopyState={handleCopyPluginState}
                                                                        activeBreakpoint={activeBreakpoint}
                                                                    />
                                                                ))}                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default VariantPropertiesPanel;